import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Typography } from "@mui/material";
import { signInWithGoogle } from "../helpers/auth";
import Page from "./Page";
import { dispatch } from "../redux/store";
import { setUser } from "../redux/actions/user";
import FirebaseLogo from "../assets/firebase-logo.svg";
import LoadingScreen from "../components/loading/LoadingScreen";

const LoginPage = () => {
  const navigate = useNavigate();
  const { authState, loadingUser } = useSelector((state) => state.user);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Redirect user to dashboard if they are already logged in
  useEffect(() => {
    if (authState === "authenticated") {
      navigate("/dashboard", { replace: true });
    } else if (authState === "unauthenticated" && isLoggingIn) {
      // Reset loading state if authentication failed
      setIsLoggingIn(false);
    }
  }, [authState, navigate, isLoggingIn]);

  const onGoogleLogin = async () => {
    try {
      setIsLoggingIn(true);
      const user = await signInWithGoogle();
      const { uid, email, displayName } = user;
      dispatch(
        setUser({
          userId: uid,
          email,
          displayName,
        }),
      );
      // The loading state will be cleared by the authentication flow
      // and the user will be redirected by the useEffect above
    } catch (e) {
      console.error(e);
      setIsLoggingIn(false);
    }
  };

  if (isLoggingIn || loadingUser) {
    return <LoadingScreen />;
  }

  return (
    <Page className="login-page" pageKey="login">
      <div className={"login-page__container"}>
        <Typography variant="pageTitleDark" className="login-page__title">
          Login or Signup
        </Typography>
        <Typography variant="p1" className="login-page__description">
          Sign in to access your tasks, sync across devices, and unlock the full
          Mindstack experience
        </Typography>
        {Capacitor.isNativePlatform() ? (
          <div className="login-page__native-login-container">
            <button
              className="login-page__google-button"
              onClick={onGoogleLogin}
              disabled={isLoggingIn || loadingUser}
            >
              <img
                className="login-page__google-button-logo"
                src="https://img.icons8.com/color/48/000000/google-logo.png"
                alt="Google Logo"
              />
              <span className="login-page__google-button-text">
                Login with Google
              </span>
            </button>
          </div>
        ) : (
          <div className="login-page__web-login-container">
            <button
              className="login-page__google-button"
              onClick={onGoogleLogin}
              disabled={isLoggingIn || loadingUser}
            >
              <img
                className="login-page__google-button-logo"
                src="https://img.icons8.com/color/48/000000/google-logo.png"
                alt="Google Logo"
              />
              <span className="login-page__google-button-text">
                Login with Google
              </span>
            </button>
          </div>
        )}

        <div className="login-page__firebase-disclaimer">
          <img
            className="login-page__firebase-logo"
            src={FirebaseLogo}
            alt="Firebase Logo"
          />
          <Typography variant="p1" className="login-page__sub-text">
            Our authentication is securely handled by Google Firebase.
          </Typography>
          <Typography variant="p1" className="login-page__sub-text">
            We do not handle any of your login information directly.
          </Typography>
        </div>
      </div>
    </Page>
  );
};

export default LoginPage;
